import { useRef, useState, type PointerEvent, type ReactNode } from 'react';

interface ResizableDetailLayoutProps {
  detail: ReactNode;
  list: ReactNode;
  isOpen: boolean;
  minDetailWidth?: number;
  maxDetailWidth?: number;
  defaultDetailWidth?: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export function ResizableDetailLayout({
  detail,
  list,
  isOpen,
  minDetailWidth = 360,
  maxDetailWidth = 760,
  defaultDetailWidth = 520,
}: ResizableDetailLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [detailWidth, setDetailWidth] = useState(defaultDetailWidth);
  const [isDragging, setIsDragging] = useState(false);

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const maxWidth = Math.max(minDetailWidth, Math.min(maxDetailWidth, rect.width - 420));
    setDetailWidth(clamp(rect.right - event.clientX, minDetailWidth, maxWidth));
  };

  if (!isOpen) {
    return <>{list}</>;
  }

  return (
    <div
      ref={containerRef}
      className="flex h-screen min-w-0 overflow-hidden bg-white max-lg:block"
      onPointerMove={handlePointerMove}
      onPointerUp={() => setIsDragging(false)}
      onPointerCancel={() => setIsDragging(false)}
    >
      <main className="min-w-0 flex-1 overflow-y-auto bg-admin-surface">
        {list}
      </main>

      <div
        role="separator"
        aria-orientation="vertical"
        tabIndex={0}
        className={`w-2 shrink-0 cursor-col-resize bg-admin-border/60 transition hover:bg-admin-accent/50 max-lg:hidden ${
          isDragging ? 'bg-admin-accent/60' : ''
        }`}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          setIsDragging(true);
        }}
      />

      <aside
        className="min-w-0 shrink-0 overflow-y-auto border-l border-admin-border bg-white max-lg:w-full max-lg:border-l-0 max-lg:border-t"
        style={{ width: detailWidth }}
      >
        {detail}
      </aside>
    </div>
  );
}
